import { Card, CardContent } from "../../ui/card";
import { motion } from "framer-motion";
import { SummaryHeader } from "../../ui/SummaryHeader";
import { cn } from "../../../utils/utils";
import DataLoader from "../../../components/loaders/DataLoader";
import { endOfMonth, format, startOfMonth } from "date-fns";
import { useMemo, useState } from "react";
import { useEcommSales } from "../../e-commerce/hooks/useSalesEcomm";
import { formatCurrency } from "../../../utils/formatCurrency";
import { MonthYearPicker } from "../../ui/month-Year-Picker";



const container = {
  hidden: {},
  show: {
    transition: {
      staggerChildren: 0.1,
    },
  },
};

interface TopProductsProps {
  selectedMonth: string;
}

const item = {
  hidden: { opacity: 0, y: 40 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "backOut" } },
};

export function RetailShopifyTopProducts({ selectedMonth }: TopProductsProps) {
  const now = new Date();
    const [search, setSearch] = useState("");
    // initialize picker from incoming selectedMonth (if provided) otherwise current month
    const initialDate = selectedMonth ? (() => {
      const [year, month] = selectedMonth.split('-').map(Number);
      return new Date(year, month - 1, 1); // month - 1 because Date months are 0-indexed but yyyy-MM string is 1-indexed
    })() : new Date(now.getFullYear(), now.getMonth());
    const [selectedDate, setSelectedDate] = useState<Date | undefined>(initialDate);
  
    // derive api start/end from the picker (MonthYearPicker)
    const { startDate, endDate } = useMemo(() => {
      const monthDate = selectedDate ?? new Date(now.getFullYear(), now.getMonth());
      return {
        startDate: format(startOfMonth(monthDate), "yyyy-MM-dd"),
        endDate: format(endOfMonth(monthDate), "yyyy-MM-dd"),
      };
    }, [selectedDate, now]);
  
  const { data, isLoading } = useEcommSales(startDate, endDate);

  // Filter products by title
  const filteredProducts = data?.shopify_top_products_performance?.filter(item =>
    item.title.toLowerCase().includes(search.toLowerCase())
  ) ?? [];

  return (
    <div className="flex flex-col h-full">
      <SummaryHeader title="Top Products" />
      <Card className="flex-1 flex flex-col">
        <div className="flex items-center justify-between m-4">
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by product name..."
            className="w-[250px] border-border rounded h-8 px-4 text-left text-sm align-middle text-muted-foreground"
          />
          <div className="pr-3"><MonthYearPicker selected={selectedDate} onChange={setSelectedDate} /></div>
        </div>
        <CardContent className="flex-1 flex flex-col">
          {isLoading ? (
            <DataLoader variant="top-products" itemCount={6} />
          ) : (
            <div className="flex-1 max-h-[400px] overflow-y-auto space-y-2">
              <motion.div
                variants={container}
                initial="hidden"
                animate="show"
                className="space-y-2 p-2"
              >
                {filteredProducts.map((itemData) => (
                  <motion.div
                    key={itemData.style}
                    variants={item}
                    className="rounded-lg flex items-center justify-between gap-4 group hover:scale-[102%] hover:bg-accent p-2"
                  >
                    <div className="flex items-center gap-4">
                      <img
                        src={itemData.image_src ?? "/T-Logo.png"}
                        alt={itemData.title}
                        className="h-12 w-12 object-cover shadow-xl-soft"
                      />
                      <div className="space-y-1">
                        <div className="font-medium">{itemData.title}</div>
                        <div className="text-xs text-muted-foreground">
                          Price: {formatCurrency(itemData.average_price)} · Revenue: {formatCurrency(itemData.revenue)}
                        </div>
                      </div>
                    </div>
                    <div
                      className={cn(
                        "rounded-md px-1.5 py-0.5 text-xs whitespace-nowrap",
                        itemData.units_sold > 0
                          ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300"
                          : "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300"
                      )}
                    >
                      Sold: {itemData.units_sold}
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
